import { useEffect, useRef, useState } from "react";
import { MessageSquareText, Send, X, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "fancy-saloon-chat-session";

const QUICK_PROMPTS = [
  "What are your prices?",
  "Suggest a hairstyle for me",
  "Which product for dandruff?",
  "Kya aap aaj khule hain?",
];

const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const Bubble = ({ role, content, streaming }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${role}`}>
    <div
      className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap rounded-2xl ${
        role === "user"
          ? "bg-rose-600 text-white rounded-br-sm"
          : "bg-slate-800 text-slate-100 border border-slate-700 rounded-bl-sm"
      }`}
    >
      {content}
      {streaming && <span className="inline-block w-1.5 h-4 ml-0.5 align-middle bg-rose-400 animate-pulse rounded-sm" />}
    </div>
  </div>
);

export const SalonAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    sessionId.current = getSessionId();
    fetch(`${API}/chat/${sessionId.current}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages.map(({ role, content }) => ({ role, content }))))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = text.trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);

    const appendDelta = (delta) =>
      setMessages((m) => {
        const next = [...m];
        const last = { ...next[next.length - 1] };
        last.content += delta;
        next[next.length - 1] = last;
        return next;
      });

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let errored = false;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const evt = JSON.parse(part.slice(6));
          if (evt.delta) appendDelta(evt.delta);
          if (evt.error) errored = true;
        }
      }
      if (errored) appendDelta("Sorry, I could not answer right now. Please call or WhatsApp 9682675268.");
    } catch {
      appendDelta("Sorry, I could not connect. Please call or WhatsApp 9682675268.");
    } finally {
      setMessages((m) => m.map((x) => ({ ...x, streaming: false })));
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close salon assistant" : "Open salon assistant"}
        data-testid="assistant-toggle-btn"
        className="fixed bottom-5 right-24 z-50 flex items-center gap-2 h-14 px-5 rounded-full bg-slate-900 border border-rose-500/40 text-rose-300 shadow-lg shadow-black/40 hover:border-rose-400 hover:text-rose-200 transition-colors"
      >
        {open ? <X className="w-5 h-5" /> : <MessageSquareText className="w-5 h-5" />}
        <span className="text-xs font-bold uppercase tracking-wider hidden sm:inline">{open ? "Close" : "Ask AI"}</span>
      </button>

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-24 right-5 z-50 w-[min(92vw,380px)] h-[min(70vh,560px)] flex flex-col rounded-3xl bg-slate-950/95 backdrop-blur-xl border border-slate-800 shadow-2xl shadow-black/60 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-rose-600/20 border border-rose-500/40 flex items-center justify-center text-rose-300">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-bold text-white">Fancy Saloon Assistant</div>
              <div className="text-[11px] text-slate-400">Style advice, products, prices &amp; timings</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="space-y-3">
                <Bubble role="assistant" content="Hi! Ask me about our services, prices, timings, or describe your hair and I will suggest a style and product. You can write in English, Hindi, Urdu or Kashmiri." />
                <div className="flex flex-wrap gap-2 pt-1">
                  {QUICK_PROMPTS.map((q, i) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => send(q)}
                      data-testid={`assistant-quick-${i + 1}`}
                      className="text-[11px] px-3 py-1.5 rounded-full border border-slate-700 text-slate-300 hover:border-rose-500/60 hover:text-rose-200 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} content={m.content} streaming={m.streaming} />
            ))}
            <div ref={bottomRef} />
          </div>

          <form
            className="p-3 border-t border-slate-800 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question..."
              disabled={busy}
              data-testid="assistant-input"
              className="flex-1 px-4 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-white text-sm focus:outline-none focus:border-rose-500 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              aria-label="Send"
              data-testid="assistant-send-btn"
              className="w-10 h-10 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white flex items-center justify-center transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
